import type { EditorHost } from '@blocksuite/block-std';
import type { RootService } from '@blocksuite/blocks';
import { UnauthorizedError } from '@blocksuite/blocks';
import { assertExists } from '@blocksuite/global/utils';
import { notify } from '@pique/component';
import { authAtom, type AuthAtom, openSettingModalAtom } from '@pique/core/atoms';
import { AIProvider } from '@pique/core/blocksuite/presets/ai';
import { ImageSourceManager } from '@pique/core/blocksuite/presets/ai/image-sources/manager';
import { generateAttendeeContext } from '@pique/core/modules/properties/services/attendee-id-utils';
import type { SerializedAttendee } from '@pique/core/modules/properties/services/schema';
import { posthog } from '@pique/core/posthog';
import {
  type getCopilotHistoriesQuery,
  type RequestOptions,
} from '@pique/graphql';
import { Trans } from '@pique/i18n';
import { getCurrentStore } from '@toeverything/infra';
import { z } from 'zod';

import type { TranscriptionService as PropertiesTranscriptionService } from '../../../../modules/properties/services/transcription-service';
import type { PromptKey } from './prompt';
import {
  cleanupSessions,
  createChatSession,
  forkCopilotSession,
  listHistories,
  listHistoryIds,
  textToText,
  toImage,
} from './request';
import { setupTracker } from './tracker';

const processTypeToPromptName = new Map(
  Object.entries({
    Clearer: 'debug:action:fal-upscaler',
    'Remove background': 'debug:action:fal-remove-bg',
    'Convert to sticker': 'debug:action:fal-face-to-sticker',
  })
);

function setupAIProvider() {
  // a single workspace should have only a single chat session
  // user-id:workspace-id:doc-id -> chat session id
  const chatSessions = new Map<string, Promise<string>>();

  async function getChatSessionId(workspaceId: string, docId: string) {
    const userId = (await AIProvider.userInfo)?.id;

    if (!userId) {
      throw new UnauthorizedError();
    }

    const storeKey = `${userId}:${workspaceId}:${docId}`;
    if (!chatSessions.has(storeKey)) {
      chatSessions.set(
        storeKey,
        createChatSession({
          workspaceId,
          docId,
        })
      );
    }
    try {
      const sessionId = await chatSessions.get(storeKey);
      assertExists(sessionId);
      return sessionId;
    } catch (err) {
      // do not cache the error
      chatSessions.delete(storeKey);
      throw err;
    }
  }

  //#region actions
  AIProvider.provide('chat', options => {
    const sessionId =
      options.sessionId ?? getChatSessionId(options.workspaceId, options.docId);

    // Prefer transcript of current doc as first-class context (passed via params only)
    const transcript = getTranscriptTextSafely(options.host);

    // Generate context hint with user and attendee info
    const contextHint = generateContextHint(options.host);

    const combinedParams: Record<string, unknown> = { transcript };
    if (typeof (options as any).params === 'object' && (options as any).params) {
      Object.assign(combinedParams, (options as any).params);
    }

    // Add context hint to existing systemHint or create new one
    const existingSystemHint = (options as any).params?.systemHint || '';
    const enhancedSystemHint = contextHint
      ? (existingSystemHint ? `${existingSystemHint}\n\n${contextHint}` : contextHint)
      : existingSystemHint;

    if (enhancedSystemHint) {
      combinedParams['systemHint'] = enhancedSystemHint;
    }

    return textToText({
      ...options,
      content: options.input,
      // Cast to the request shape; downstream supports an open params bag
      params: combinedParams as Record<string, string>,
      sessionId,
    });
  });

  AIProvider.provide('brainstormMindmap', options => {
    return textToText({
      ...options,
      content: compactLargeText(options.content || ''),
      promptName: 'workflow:brainstorm',
      workflow: true,
    });
  });

  AIProvider.provide('createQuiz', options => {
    return textToText({
      ...options,
      content: options.input,
      promptName: 'workflow:quiz',
      workflow: true,
    });
  });

  AIProvider.provide('createMermaid', options => {
    return textToText({
      ...options,
      content: options.content,
      promptName: 'workflow:mermaid',
      workflow: true,
    });
  });

  AIProvider.provide('expandMindmap', options => {
    if (options.input) {
      return textToText({
        ...options,
        params: {
          mindmap: options.mindmap,
          node: options.input,
        },
        content: compactLargeText(options.input || ''),
        promptName: 'Expand mind map',
      });
    } else if (options.content) {
      return textToText({
        ...options,
        params: {
          mindmap: options.mindmap,
        },
        content: compactLargeText(options.content || ''),
        promptName: 'workflow:expandmindmap',
        workflow: true,
      });
    } else {
      throw new Error('expandMindmap action requires either input or content');
    }
  });

  AIProvider.provide('makeItReal', options => {
    let promptName: PromptKey = 'Make it real';
    let content = options.content || '';

    // wireframes
    if (options.attachments?.length) {
      content = `Here are the latest wireframes. Could you make a new website based on these wireframes and notes and send back just the html file?
Here are our design notes:\n ${content}.`;
    } else {
      // notes
      promptName = 'Make it real with text';
      content = `Here are the latest notes: \n ${content}.
Could you make a new website based on these notes and send back just the html file?`;
    }

    return textToText({
      ...options,
      content,
      promptName,
    });
  });

  AIProvider.provide('createSlides', options => {
    const SlideSchema = z.object({
      page: z.number(),
      type: z.enum(['name', 'title', 'content']),
      content: z.string(),
    });
    type Slide = z.infer<typeof SlideSchema>;
    const parseJson = (json: string) => {
      try {
        return SlideSchema.parse(JSON.parse(json));
      } catch {
        return null;
      }
    };
    // TODO(@darkskygit): move this to backend's workflow after workflow support custom code action
    const postfix = (text: string): string => {
      const slides = text
        .split('\n')
        .map(parseJson)
        .filter((v): v is Slide => !!v);
      return slides
        .map(slide => {
          if (slide.type === 'name') {
            return `- ${slide.content}`;
          } else if (slide.type === 'title') {
            return `  - ${slide.content}`;
          } else if (slide.content.includes('\n')) {
            return slide.content
              .split('\n')
              .map(c => `    - ${c}`)
              .join('\n');
          } else {
            return `    - ${slide.content}`;
          }
        })
        .join('\n');
    };
    return textToText({
      ...options,
      content: options.content,
      promptName: 'workflow:presentation',
      workflow: true,
      postfix,
    });
  });

  AIProvider.provide('createImage', options => {
    // test to image
    let promptName: PromptKey = 'debug:action:dalle3';
    // image to image
    if (options.attachments?.length) {
      promptName = 'debug:action:fal-sd15';
    }
    return toImage({
      ...options,
      promptName,
    });
  });


  AIProvider.provide('processImage', options => {
    // test to image
    const promptKey = processTypeToPromptName.get((options as unknown as { type?: string }).type ?? '') as PromptKey;
    return toImage({
      ...options,
      timeout: 600000, // 10 minutes for image processing
      promptName: promptKey,
    });
  });



  AIProvider.provide('oneOffRequest', (options: BlockSuitePresets.OneOffRequestOptions) => {
    const raw = options.content || options.input || '';
    const content = compactLargeText(raw);

    // Generate context hint with user and attendee info
    const contextHint = generateContextHint(options.host);

    const combinedParams: Record<string, unknown> = {};
    if (typeof (options as any).params === 'object' && (options as any).params) {
      Object.assign(combinedParams, (options as any).params);
    }

    // Add context hint to existing systemHint or create new one
    const existingSystemHint = (options as any).params?.systemHint || '';
    const enhancedSystemHint = contextHint
      ? (existingSystemHint ? `${existingSystemHint}\n\n${contextHint}` : contextHint)
      : existingSystemHint;

    if (enhancedSystemHint) {
      combinedParams['systemHint'] = enhancedSystemHint;
    }

    // Encourage structured (non-fenced) JSON by setting a specific prompt name the backend knows
    return textToText({
      ...options,
      content,
      params: combinedParams as Record<string, string>,
      promptName: 'oneoff:augmentation',
      workflow: false,
    });
  });


  AIProvider.provide('histories', {
    chats: async (
      workspaceId: string,
      docId?: string,
      options?: RequestOptions<
        typeof getCopilotHistoriesQuery
      >['variables']['options']
    ): Promise<BlockSuitePresets.AIHistory[]> => {
      return ((await listHistories(workspaceId, docId, options)) ?? []) as BlockSuitePresets.AIHistory[];
    },
    cleanup: async (
      workspaceId: string,
      docId: string,
      sessionIds: string[]
    ) => {
      await cleanupSessions({ workspaceId, docId, sessionIds });
    },
    ids: async (
      workspaceId: string,
      docId?: string,
      options?: RequestOptions<
        typeof getCopilotHistoriesQuery
      >['variables']['options']
    ): Promise<BlockSuitePresets.AIHistoryIds[]> => {
      return (await listHistoryIds(workspaceId, docId, options)) as BlockSuitePresets.AIHistoryIds[];
    },
  });

  AIProvider.provide('photoEngine', {
    async searchImages(options): Promise<string[]> {
      const manager = ImageSourceManager.getInstance();
      const results = await manager.searchImages({
        query: options.query,
        width: options.width,
        height: options.height,
      });
      return results.map(r => r.url);
    },
    async searchVectors(options: {
      query: string;
    }): Promise<string[]> {
      const manager = ImageSourceManager.getInstance();
      const results = await manager.searchVectors({
        query: options.query,
      });
      return results.map(r => r.url);
    },
  });

  // AI onboarding removed

  AIProvider.provide('forkChat', options => {
    return forkCopilotSession(options);
  });

  AIProvider.slots.requestUpgradePlan.on(() => {
    getCurrentStore().set(openSettingModalAtom, {
      activeTab: 'billing',
      open: true,
    });
    posthog.track('paywall_viewed', { context: 'ai_paywall' });
  });

  AIProvider.slots.requestLogin.on(() => {
    const currentAuth = getCurrentStore().get(authAtom);
    (getCurrentStore().set as (atom: typeof authAtom, value: AuthAtom) => void)(
      authAtom,
      { ...currentAuth, openModal: true, state: 'signIn' as const }
    );
  });

  AIProvider.slots.requestRunInEdgeless.on(() => {
    notify.warning({
      title: (
        <Trans i18nKey="com.pique.ai.action.edgeless-only.dialog-title" />
      ),
    });
  });

  setupTracker();
}

export { setupAIProvider };

// Local utility to safely grab transcript text from current host
function getTranscriptTextSafely(host: EditorHost | undefined): string {
  try {
    if (!host) return '';
    const pageService = host.spec.getService<RootService>('affine:page');
    const propertiesSvc = pageService?.transcriptionService as
      | PropertiesTranscriptionService
      | undefined;
    if (!propertiesSvc) return '';
    // Use the shared summary-or-transcript method used elsewhere for consistency
    const text = propertiesSvc.getPageSummaryOrTranscript(host.doc.id) ?? '';
    return typeof text === 'string' ? text.trim() : '';
  } catch {
    return '';
  }
}

// Get current user info safely
function getCurrentUserSafely(host: EditorHost | undefined) {
  try {
    if (!host) return null;
    const pageService = host.spec.getService<RootService>('affine:page');
    return (pageService as any)?.currentUser || null;
  } catch {
    return null;
  }
}

// Get page attendees safely
function getPageAttendeesSafely(host: EditorHost | undefined): SerializedAttendee[] {
  try {
    if (!host) return [];
    const pageService = host.spec.getService<RootService>('affine:page');
    const attendeesService = (pageService as any)?.attendeesService;
    if (!attendeesService?.getPageAttendees) return [];
    return attendeesService.getPageAttendees(host.doc.id) || [];
  } catch {
    return [];
  }
}

// Generate context hint with user and attendee information using centralized utility
function generateContextHint(host: EditorHost | undefined): string {
  const currentUser = getCurrentUserSafely(host);
  const attendees = getPageAttendeesSafely(host);

  return generateAttendeeContext({
    currentUser,
    attendees,
    includeTranscriptionNote: true,
  });
}

// Compact large free-form text (e.g., full transcripts) without changing paragraph layout
function compactLargeText(input: string): string {
  if (!input) return '';
  // Normalize newlines and tabs first
  let text = input.replace(/\r\n?/g, '\n').replace(/\t/g, ' ');
  // Trim trailing spaces for each line
  text = text
    .split('\n')
    .map(line => line.replace(/[ \t]+$/g, ''))
    .join('\n');
  // Collapse excessive internal spaces (3+ to 1) but keep single spaces
  text = text.replace(/ {3,}/g, ' ');
  // Collapse multiple blank lines to a single blank line
  text = text.replace(/\n{3,}/g, '\n\n');
  // Final trim
  return text.trim();
}
